import { GrElement, TOKENS_CSS } from "../core/base";
import { deviceSignal, formatSignalValue } from "../core/endpoints";
import type { DeviceDescriptor } from "../core/messages";
import { buildSourcePicker, ENDPOINT_CSS, renderRows } from "./endpoint-shared";

/**
 * <gr-input device="pot" transport="serial" baud="115200" label="Slide pot"></gr-input>
 * <gr-input device="room" transport="mqtt" url="wss://…" topic="room/#" signals="temp,rh" auto></gr-input>
 *
 * Where the page's data comes in, as one block: pick a source, connect it,
 * and watch the signals land. It wraps `<gr-connect>` — same transports,
 * same stored per-page override — and adds the two things a first-time
 * visitor needs, a visible source choice and proof that data is flowing.
 *
 * Signals publish on `<device>/<signal>` exactly as they do without this
 * element, so the rest of the page subscribes to the bus and never touches
 * the block. `signals="a,b"` pins the readout to a known set (and shows the
 * rows greyed before the first reading); otherwise every signal that
 * arrives gets a row.
 */
export class GrInput extends GrElement {
  private rows!: HTMLElement;
  private state!: HTMLElement;
  private values = new Map<string, string>();
  private units = new Map<string, string>();
  private paint = 0;

  connectedCallback(): void {
    const root = this.attachShadow({ mode: "open" });
    root.innerHTML = `
      <style>${TOKENS_CSS}${ENDPOINT_CSS}</style>
      <header>
        <span class="label">input</span>
        <span class="title"></span>
        <span class="state">idle</span>
      </header>
      <div class="body">
        <div class="rows"></div>
      </div>
    `;
    this.rows = root.querySelector(".rows")!;
    this.state = root.querySelector(".state")!;
    root.querySelector(".title")!.textContent = this.attr("label", this.device);

    const picker = buildSourcePicker(this, this.attr("connect-label", "Connect"));
    root.querySelector(".body")!.insertBefore(picker.element, this.rows);

    for (const signal of this.declared()) this.values.set(signal, "—");
    this.render();

    this.track(
      this.bus.subscribe(`${this.device}/+`, (msg) => {
        const signal = deviceSignal(this.device, msg.topic);
        if (signal === null) return;
        if (this.declared().length > 0 && !this.declared().includes(signal)) return;
        this.values.set(signal, formatSignalValue(msg.payload));
        this.schedule();
      }),
    );
    this.track(
      this.bus.subscribe(`${this.device}/$state`, (msg) => {
        const state = String(msg.payload);
        this.setAttribute("data-state", state);
        this.state.textContent = state === "idle" ? "idle" : state;
        if (state === "disconnected" || state === "error") this.stale();
      }),
    );
    this.track(
      this.bus.subscribe(`${this.device}/$descriptor`, (msg) => {
        this.applyDescriptor(msg.payload as unknown as DeviceDescriptor | null);
      }),
    );
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    cancelAnimationFrame(this.paint);
    this.paint = 0;
  }

  private get device(): string {
    return this.attr("device", "device");
  }

  private declared(): string[] {
    return this.attr("signals")
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean);
  }

  private applyDescriptor(descriptor: DeviceDescriptor | null): void {
    if (!descriptor) return;
    if (descriptor.name && !this.getAttribute("label")) {
      this.shadowRoot!.querySelector(".title")!.textContent = descriptor.name;
    }
    for (const entry of descriptor.signals ?? []) {
      if (entry.unit) this.units.set(entry.key, entry.unit);
      if (!this.values.has(entry.key) && this.declared().length === 0) {
        this.values.set(entry.key, "—");
      }
    }
    this.schedule();
  }

  private stale(): void {
    for (const key of this.values.keys()) this.values.set(key, "—");
    this.schedule();
  }

  /** Firmware can stream faster than the display is worth repainting. */
  private schedule(): void {
    if (this.paint) return;
    this.paint = requestAnimationFrame(() => {
      this.paint = 0;
      this.render();
    });
  }

  private render(): void {
    renderRows(
      this.rows,
      [...this.values].map(([key, value]) => ({
        key,
        value: value === "—" ? value : `${value}${this.units.get(key) ? ` ${this.units.get(key)}` : ""}`,
        stale: value === "—",
      })),
      "Connect a source — signals appear here as they arrive.",
    );
  }
}

customElements.define("gr-input", GrInput);
